import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type FnScore = {
  function: DomainCode;
  name: string;
  score: number;
  weight: number;
  weightedScore: number;
  totalQuestions: number;
  answeredQuestions: number;
};

export type OverallScore = {
  overallScore: number;
  totalQuestions: number;
  answeredQuestions: number;
};

type DomainCode = "GV" | "ID" | "PR" | "DE" | "RS" | "RC";

type State = {
  byFn: FnScore[] | null;
  overall: OverallScore | null;
  loading: boolean;
  error: string | null;
};

const DOMAIN_LABELS: Record<DomainCode, string> = {
  GV: "Govern",
  ID: "Identify",
  PR: "Protect",
  DE: "Detect",
  RS: "Respond",
  RC: "Recover",
};

const DOMAIN_ORDER: DomainCode[] = ["GV", "ID", "PR", "DE", "RS", "RC"];

const isDev = import.meta.env.DEV;

export function useSelfAssessmentScores(assessmentId: string | null) {
  const [state, setState] = useState<State>({
    byFn: null,
    overall: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!assessmentId) {
        if (!cancelled) setState(s => ({ ...s, loading: false, error: "No assessmentId provided" }));
        return;
      }

      setState(s => ({ ...s, loading: true, error: null }));

      try {
        // Use RPCs only — no raw table access
        const [scoresResult, responsesResult] = await Promise.all([
          supabase.rpc("respondent_get_scores_by_function", { p_assessment_id: assessmentId }),
          supabase.rpc("respondent_get_responses", { p_assessment_id: assessmentId }),
        ]);

        if (scoresResult.error) {
          if (isDev) console.warn("[useSelfAssessmentScores] scores RPC error:", scoresResult.error);
          if (!cancelled) setState({ byFn: null, overall: null, loading: false, error: scoresResult.error.message });
          return;
        }

        if (responsesResult.error) {
          if (isDev) console.warn("[useSelfAssessmentScores] responses RPC error:", responsesResult.error);
          if (!cancelled) setState({ byFn: null, overall: null, loading: false, error: responsesResult.error.message });
          return;
        }

        const scoresData = scoresResult.data ?? [];
        const responsesData = responsesResult.data ?? [];

        if (isDev) {
          console.log("[useSelfAssessmentScores] scores rows:", scoresData.length, "responses rows:", responsesData.length);
        }

        // If no responses at all, return empty but not error
        if (responsesData.length === 0) {
          if (isDev) console.log("[useSelfAssessmentScores] no responses — returning empty");
          if (!cancelled) setState({ byFn: [], overall: { overallScore: 0, totalQuestions: 0, answeredQuestions: 0 }, loading: false, error: null });
          return;
        }

        // Build domain map from RPC results: { domain -> { avg_score, question_count, answered_count } }
        const domainMap = new Map<string, { avg_score: number; question_count: number; answered_count: number }>();
        for (const row of scoresData) {
          domainMap.set(row.domain.toUpperCase(), {
            avg_score: row.avg_score ?? 0,
            question_count: row.question_count ?? 0,
            answered_count: row.answered_count ?? 0,
          });
        }

        // Build FnScore array in canonical order, include only domains that have questions
        const byFn: FnScore[] = DOMAIN_ORDER
          .filter(code => domainMap.has(code))
          .map(code => {
            const d = domainMap.get(code)!;
            return {
              function: code,
              name: DOMAIN_LABELS[code],
              score: d.avg_score,
              weight: 1,
              weightedScore: d.avg_score,
              totalQuestions: d.question_count,
              answeredQuestions: d.answered_count,
            };
          });

        // Compute overall
        let totalQuestions = 0;
        let answeredQuestions = 0;
        let scoreSum = 0;
        let scoredDomains = 0;
        for (const fn of byFn) {
          totalQuestions += fn.totalQuestions;
          answeredQuestions += fn.answeredQuestions;
          if (fn.answeredQuestions > 0) {
            scoreSum += fn.score;
            scoredDomains++;
          }
        }

        const overallScore = scoredDomains > 0 ? scoreSum / scoredDomains : 0;

        if (!cancelled) {
          setState({
            byFn,
            overall: { overallScore, totalQuestions, answeredQuestions },
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        if (isDev) console.error("[useSelfAssessmentScores] unexpected error:", error);
        if (!cancelled) {
          setState({
            byFn: null,
            overall: null,
            loading: false,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, [assessmentId]);

  return state;
}
